import React from "react";
import PropTypes from "prop-types";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import Icon from "@src/components/atoms/Icon";
import Tooltip from "@src/components/atoms/Tooltip";
import DialogBubble from "@src/components/atoms/DialogBubble";
import moment from "moment";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import { useIsMobileWidth } from "@src/utils";
import { SignolUtils } from "@src/utils";

const formatTimelineEvents = (events, targetAcronym, periodRange) => {
  const filteredEvents = events.filter((event) =>
    event.behaviours.some(
      (behaviour) => behaviour.behaviourAcronym === targetAcronym,
    ),
  );

  const daysInPeriod = Array.from(
    { length: periodRange.daysInPeriod },
    (_, i) => moment.utc(periodRange?.from).add(i, "days").date(),
  );

  const eventsByDay = daysInPeriod.map((day) => {
    const eventsForDay = filteredEvents.filter(
      (event) => moment.utc(event.ocurrenceDatetime).date() === day,
    );

    return { day, data: eventsForDay };
  });

  return eventsByDay;
};

const Timeline = (props) => {
  const {
    events,
    target,
    periodKey,
    pRange,
    getDynamicContent,
    industry,
    isOpen,
    customerCode,
    predictionsEnabled = false,
    handleBubbleClick,
    periodName = "period",
    preferredEventName,
    digDeeperDismissed = false,
    onDismissDigDeeper,
  } = props;

  const isMobile = useIsMobileWidth();
  const hasData = events.length > 0 || target.total > 0;
  const bvrColor = getBehaviourColourForPosition(target.order);

  const isShipping = industry === "shipping";

  const periodRange = pRange
    ? pRange
    : AppUtils.getDateRangeFromPeriodKey(periodKey);
  const periodLabel = moment.utc(periodRange?.from).format("D MMMM YYYY");
  const isOpenPeriod = isOpen;

  const daysInMonth = periodRange?.daysInPeriod;

  const timelineDays = formatTimelineEvents(
    events,
    target?.behaviourAcronym,
    periodRange,
  );

  const firstDataDayIdx = timelineDays.findIndex((d) => d.data.length > 0);
  // DialogBubble portals to document body and positions itself from the
  // target's measured screen position, so it isn't bound by the timeline's
  // horizontally-scrolling container — no layout compensation needed here.
  const DIG_DEEPER_DIRECTION = "down-right";

  const bubbles = (data, idx) => {
    return (
      <div key={idx} className={`mx-4 px-1 border-l-2 border-silver-300`}>
        {data.map((ev, innerIdx) => {
          const bvr = ev.behaviours.find(
            (b) => b.behaviourAcronym === target.behaviourAcronym,
          );
          if (!bvr) {
            return null;
          }

          const usingPrediction = !!predictionsEnabled && !!bvr.usingPrediction;
          const isSuccess = !!bvr.isSuccess;
          const stateIcon = AppUtils.getStateIcon(bvr.state);
          const isExclusion =
            bvr.state === "EXCLUSION" ||
            (bvr.hasReasons && !bvr?.usingSelfReport);
          const wasPastSelfReportable =
            bvr?.state === "INCOMPLETE" &&
            bvr?.isSelfReportable &&
            bvr?.succeededSelfReported === null &&
            !isOpenPeriod;
          const bubbleColor =
            isExclusion || wasPastSelfReportable ? "neutral" : bvrColor.name;
          const iconBgColor = usingPrediction ? "black" : `${bubbleColor}-200`;
          const iconTextColor = usingPrediction
            ? `${bubbleColor}-200`
            : "black";
          const bgColorIntensity = isSuccess ? "500" : "100";
          const textColor = isSuccess ? "white" : "black";

          const isClickable = typeof handleBubbleClick === "function";
          const isFirstVisibleBubble =
            idx === firstDataDayIdx && innerIdx === 0;
          const card = (
            <div
              className={`py-1 px-2 my-1 space-x-1 text-center text-sm flex items-center justify-evenly rounded-lg
                  bg-${bubbleColor}-${bgColorIntensity} text-${textColor} hover:shadow-lg
                  ${isClickable ? "cursor-pointer" : ""}
                  ${isShipping ? "" : "w-28"}
                `}
              {...(isClickable
                ? {
                    onClick: () => handleBubbleClick(ev),
                    ...SignolUtils.makeTrackableDataAttributes({
                      actionKey: "TARGETS_OPEN_EVENT_DETAILS",
                      eventId: ev.id,
                      succeeded: bvr.succeeded,
                      behaviourAcronym: bvr.behaviourAcronym,
                      behaviourState: bvr.state,
                    }),
                  }
                : {})}
            >
              <div>
                <Icon
                  name={stateIcon}
                  backgroundColor={iconBgColor}
                  textColor={iconTextColor}
                  size={"lg"}
                  centered={false}
                />
              </div>
              {!!isShipping
                ? getDynamicContent(
                    `captain__events__details__type__${
                      ev?.reportType?.toLowerCase().replace(/\s+/g, "_") ||
                      "noon"
                    }`,
                  )
                : `${customerCode}-${ev.identifier}`}
            </div>
          );

          if (isFirstVisibleBubble && !digDeeperDismissed) {
            const articleEventName = AppUtils.withIndefiniteArticle(
              preferredEventName || "flight",
            );
            return (
              <div key={innerIdx} style={{ width: "fit-content" }}>
                <DialogBubble
                  target={card}
                  direction={DIG_DEEPER_DIRECTION}
                  bgColor="primary-100"
                  onDismiss={onDismissDigDeeper}
                >
                  <p className="text-sm font-bold">
                    {getDynamicContent(
                      "captain__targets__timeline_dig_deeper__title",
                      {},
                      "Dig deeper",
                    )}
                  </p>
                  <p className="text-sm">
                    {getDynamicContent(
                      "captain__targets__timeline_dig_deeper__body",
                      { ARTICLE_EVENT_NAME: articleEventName },
                      `You can click on individual cards in this timeline to review ${articleEventName} in more detail`,
                    )}
                  </p>
                </DialogBubble>
              </div>
            );
          }

          return (
            <div key={innerIdx} style={{ width: "fit-content" }}>
              {card}
            </div>
          );
        })}
      </div>
    );
  };
  const tooltipContent = (
    <div className="flex">
      <div>
        <Icon
          className="m-2"
          name="close"
          backgroundColor="black"
          textColor={`${bvrColor.name}-200`}
          size="lg"
          centered={false}
        />
        <Icon
          className="m-2"
          name="check"
          backgroundColor="black"
          textColor={`${bvrColor.name}-200`}
          size="lg"
          centered={false}
        />
      </div>
      <div className="my-auto pl-3">
        {getDynamicContent("captain__tooltip__target_timeline", {})}
      </div>
    </div>
  );
  return (
    <div className="mx-auto text-left sm:text-left w-full p-4">
      <div className="">
        <div className="w-full flex flex-col sm:flex-row relative pr-8">
          <h3 className={`leading-snug w-full md:w-1/2`}>
            Timeline of this {periodName.toLowerCase()}
          </h3>

          {predictionsEnabled && (
            <div className="absolute w-full h-lg mt-1">
              <Tooltip content={[tooltipContent]} direction={"down-left"} />
            </div>
          )}
        </div>
        <div className="w-full h-full p-2 sm:p-4">
          <div
            id="timeline-container"
            className="mx-auto h-full overflow-x-scroll relative  timeline-container custom-scrollbar"
          >
            {isMobile && (
              <>
                {/* Mobile forced fake scrollbar track */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-white z-10 pointer-events-none p-1" />

                {/* Mobile forced fake scrollbar thumb (visual cue of static width) */}
                <div className="absolute bottom-0 left-0 w-12 h-1 bg-[#00bed6] rounded-full z-20 pointer-events-none" />
              </>
            )}
            {!hasData && (
              <div
                className={`font-bold text-sm rounded-full text-left my-2 py-2 px-4 bg-${bvrColor.name}-100`}
              >
                {getDynamicContent("captain__targets__period__empty", {
                  PERIOD: periodName.toLowerCase(),
                })}
              </div>
            )}
            <div className="items-center h-full grid">
              <div className="inline-flex">
                {timelineDays.map((d, idx) => {
                  const even = idx % 2 == 0;
                  return (
                    <div
                      key={idx}
                      className="w-36 self-end inline-flex flex-col-reverse"
                      style={{ minHeight: "6rem" }}
                    >
                      {even && d.data.length > 0 && bubbles(d.data, idx)}
                    </div>
                  );
                })}
              </div>
              <div className="flex">
                {timelineDays.map((d, idx) => {
                  const lastDay = idx === daysInMonth - 1;
                  const today = moment.utc().format("D");
                  const isCurrentPeriod = moment
                    .utc()
                    .isBetween(periodRange.from, periodRange.to, "days", "[]");
                  const isToday =
                    isCurrentPeriod && Number(today) === Number(d.day);
                  const isTomorrow =
                    isCurrentPeriod && Number(d.day) > Number(today);
                  const isPast =
                    !isCurrentPeriod || Number(d.day) < Number(today);
                  return (
                    <div
                      key={idx}
                      id={`timeline-day-${d.day}`}
                      className="flex items-center py-1"
                    >
                      <div
                        className={`flex items-center text-sm font-bold h-8 w-8 rounded-full
                            ${isPast ? `bg-${bvrColor.name}-100 text-neutral-800 border-none` : ""}
                            ${isToday ? `bg-${bvrColor.name}-600 text-white border-none` : ""}
                            ${isTomorrow ? ` bg-white text-neutral-500 border-2 border-${bvrColor.name}-500` : ""}`}
                      >
                        <span className="text-center mx-auto">{d.day}</span>
                      </div>
                      <span
                        className={`h-0 w-28 border-2 ${lastDay ? "border-white" : "border-silver-300"}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="inline-flex" style={{ minHeight: "6rem" }}>
                {timelineDays.map((d, idx) => {
                  const even = idx % 2 == 0;
                  return (
                    <div
                      key={idx}
                      className="w-36 self-start inline-flex flex-col-reverse"
                    >
                      {!even && d.data.length > 0 && bubbles(d.data, idx)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const timelineControls = {
  events: {
    propTypes: PropTypes.arrayOf(PropTypes.object),
    controlType: "object",
    description: "Events to feed in the timeline",
  },
  target: {
    propTypes: PropTypes.object,
    controlType: "object",
    description: "Events to feed in the timeline",
  },
  periodKey: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["2025-05", "2025-04", "2025-05-01", "2025-04-16"],
    description: "Period key",
  },
  customerCode: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["CHR", "VS"],
    description: "CustomerCode",
  },
  industry: COMMON_CONTROLS.industry,
  isOpen: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "is an open period or not",
  },
  predictionsEnabled: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "iare predictions enabled on the customer",
  },
  getDynamicContent: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
  preferredEventName: {
    propType: PropTypes.string,
    description:
      "Customer's preferred singular event name (e.g. 'flight', 'opportunity'), used in the dig-deeper hint",
    controlType: "text",
  },
  digDeeperDismissed: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description:
      "Whether the 'dig deeper' hint bubble has already been shown/dismissed. Owned by the parent so it can be persisted (e.g. operatorMetadata) rather than local component state.",
    defaultValue: false,
  },
  onDismissDigDeeper: {
    propType: PropTypes.func,
    description: "Called when the 'dig deeper' hint bubble is dismissed",
    disable: true,
  },
};
Timeline.propTypes = AppUtils.getPropTypeFromControls(timelineControls);

export default Timeline;
